/**
 * BenchmarkCharts.js
 * Displays line charts showing model performance over time across different benchmarks
 */

import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';

const BenchmarkCharts = ({ gameInstanceId, apiBaseUrl, users = [] }) => {
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [rawAnalyses, setRawAnalyses] = useState(null); // Store raw data for reprocessing
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBenchmark, setSelectedBenchmark] = useState(null); // Single benchmark selection
  const [selectedPoint, setSelectedPoint] = useState(null); // { benchmarkName, modelName, round, explanation } - clicked point
  const [focusedModel, setFocusedModel] = useState(null); // Track which model is focused for highlighting
  const [maxRoundFilter, setMaxRoundFilter] = useState(null); // Filter to show only rounds up to this number
  const chartRefs = useRef({}); // Store refs for each chart for screenshot capture
  const [discussionRounds, setDiscussionRounds] = useState([]); // List of discussion rounds for dropdown
  const [selectedRoundId, setSelectedRoundId] = useState(null); // Currently selected round for video stitcher
  const [benchmarkType, setBenchmarkType] = useState('cumulative'); // 'round' or 'cumulative'

  // Create a comprehensive mapping from any player name variation to model name
  const playerToModelMap = {};
  users.forEach(user => {
    if (user.name && user.model) {
      // Map full player name (e.g., "Kimi (Jinx)")
      playerToModelMap[user.name] = user.model;

      // Map base name without parentheses (e.g., "Kimi")
      const baseName = user.name.split('(')[0].trim();
      if (baseName !== user.name) {
        playerToModelMap[baseName] = user.model;
      }

      // Map short model name (e.g., "kimi-k2-thinking" from "moonshotai/kimi-k2-thinking")
      if (user.model.includes('/')) {
        const shortModel = user.model.split('/')[1];
        playerToModelMap[shortModel] = user.model;
      }
    }
  });

  // Helper function to get the full model name from any variation
  const getModelName = (name) => {
    // If it's already a full model name (contains /), use it
    if (name.includes('/')) {
      return name;
    }

    // Otherwise, look it up in the map
    return playerToModelMap[name] || name;
  };

  // Color scheme matching round analysis UI
  const colors = {
    purple: '#8b5cf6',
    darkPurple: '#5b21b6',
    lightPurple: '#c4b5fd',
    pink: '#ec4899',
    lightPink: '#f9a8d4',
    bgDark: 'rgba(10, 10, 20, 0.98)',
    bgMedium: 'rgba(15, 15, 25, 0.98)',
    bgLight: 'rgba(20, 20, 35, 0.95)'
  };

  // Model colors for charts
  const modelColors = [
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#06b6d4', // cyan
    '#8b5cf6', // purple (lighter)
  ];

  const getModelColor = (index) => {
    return modelColors[index % modelColors.length];
  };

  // Create consistent color mapping for all models across all charts
  const modelColorMap = {};
  if (benchmarkData && benchmarkData.models) {
    benchmarkData.models.forEach((modelName, index) => {
      modelColorMap[modelName] = getModelColor(index);
    });
  }

  // Fetch round analysis history
  useEffect(() => {
    const fetchBenchmarkHistory = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${apiBaseUrl}/api/takeGameStep?getRoundAnalysisHistory=true&gameInstanceId=${gameInstanceId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (!result.roundAnalyses || result.roundAnalyses.length === 0) {
          setLoading(false);
          return;
        }

        // Store raw analyses for reprocessing when benchmark type changes
        setRawAnalyses(result.roundAnalyses);

        // Process the data into a format suitable for charts
        const processedData = processRoundAnalyses(result.roundAnalyses, benchmarkType);
        setBenchmarkData(processedData);

        // Select first benchmark by default
        if (processedData && processedData.benchmarks && processedData.benchmarks.length > 0) {
          setSelectedBenchmark(processedData.benchmarks[0]);
        }
      } catch (err) {
        console.error('[BenchmarkCharts] Error fetching benchmark history:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (gameInstanceId) {
      fetchBenchmarkHistory();
    }
  }, [gameInstanceId, apiBaseUrl]);

  // Reprocess data when benchmark type changes
  useEffect(() => {
    if (rawAnalyses && rawAnalyses.length > 0) {
      console.log('[BenchmarkCharts] Reprocessing data with benchmark type:', benchmarkType);
      const processedData = processRoundAnalyses(rawAnalyses, benchmarkType);
      setBenchmarkData(processedData);

      // Keep selectedBenchmark if it still exists in new data
      if (selectedBenchmark && processedData.benchmarks.includes(selectedBenchmark)) {
        // Benchmark still exists, keep it selected
      } else if (processedData.benchmarks.length > 0) {
        // Otherwise select first benchmark
        setSelectedBenchmark(processedData.benchmarks[0]);
      }
    }
  }, [benchmarkType, rawAnalyses]);

  // Fetch discussion rounds for the dropdown
  useEffect(() => {
    const fetchDiscussionRounds = async () => {
      if (!gameInstanceId) return;

      try {
        const response = await fetch(
          `${apiBaseUrl}/api/takeGameStep?getRoundAnalysisHistory=true&gameInstanceId=${gameInstanceId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        console.log('[BenchmarkCharts] Fetched round analyses:', result.roundAnalyses?.length || 0);

        if (result.roundAnalyses && result.roundAnalyses.length > 0) {
          // Log all round types to see what we have
          console.log('[BenchmarkCharts] All round types:',
            result.roundAnalyses.map(a => a.round_type));

          // Check if any rounds have interesting_findings
          const roundsWithFindings = result.roundAnalyses.filter(analysis => {
            const hasFindings = analysis.data?.interesting_findings &&
                               analysis.data.interesting_findings.length > 0;
            return hasFindings;
          });

          console.log('[BenchmarkCharts] Rounds with interesting findings:', roundsWithFindings.length);

          // Debug: log first analysis to see structure
          if (roundsWithFindings.length > 0) {
            console.log('[BenchmarkCharts] First analysis structure:', {
              fullAnalysis: roundsWithFindings[0],
              keys: Object.keys(roundsWithFindings[0])
            });
          }

          // Use all rounds that have interesting_findings instead of filtering by type
          const discussionRoundsList = roundsWithFindings.map((analysis, index) => ({
            id: analysis.id || analysis.round_id || index, // Try multiple field names
            number: index + 1,
            type: analysis.round_type,
            createdAt: analysis.created_at,
            originalAnalysis: analysis // Keep reference for debugging
          }));

          console.log('[BenchmarkCharts] Discussion rounds list:', discussionRoundsList);

          setDiscussionRounds(discussionRoundsList);

          // Auto-select the most recent discussion round
          if (discussionRoundsList.length > 0) {
            const selectedId = discussionRoundsList[discussionRoundsList.length - 1].id;
            console.log('[BenchmarkCharts] Auto-selecting round ID:', selectedId);
            setSelectedRoundId(selectedId);
          }
        }
      } catch (err) {
        console.error('[BenchmarkCharts] Error fetching discussion rounds:', err);
      }
    };

    fetchDiscussionRounds();
  }, [gameInstanceId, apiBaseUrl]);

  // Update chart filter when selected round changes
  useEffect(() => {
    console.log('[BenchmarkCharts] === FILTER UPDATE TRIGGERED ===');
    console.log('[BenchmarkCharts] selectedRoundId:', selectedRoundId);
    console.log('[BenchmarkCharts] Current maxRoundFilter before update:', maxRoundFilter);

    if (!selectedRoundId) {
      // No round selected - show all data
      console.log('[BenchmarkCharts] No round selected, setting filter to null');
      setMaxRoundFilter(null);
      return;
    }

    // Find the round in discussionRounds list to get its number
    const selectedRound = discussionRounds.find(r => r.id === selectedRoundId);

    if (selectedRound) {
      const roundNumber = selectedRound.number;
      console.log('[BenchmarkCharts] Found round in list, number:', roundNumber);
      console.log('[BenchmarkCharts] Setting maxRoundFilter to:', roundNumber);
      setMaxRoundFilter(roundNumber);
    } else {
      console.log('[BenchmarkCharts] Round not found in list, showing all data');
      setMaxRoundFilter(null);
    }
  }, [selectedRoundId, discussionRounds]);

  // Process round analyses into chart data
  const processRoundAnalyses = (analyses, benchmarkTypeToUse) => {
    // Sort by created_at to ensure chronological order
    const sorted = analyses.sort((a, b) => {
      return new Date(a.created_at) - new Date(b.created_at);
    });

    // Extract all unique benchmarks and models
    const benchmarksSet = new Set();
    const modelsSet = new Set();
    const dataByBenchmark = {};

    // First pass: collect ALL unique benchmark names from both types
    sorted.forEach((analysis) => {
      const roundBenchmarks = analysis.data?.round_benchmarks || [];
      const cumulativeBenchmarks = analysis.data?.cumulative_benchmarks || [];

      roundBenchmarks.forEach(b => benchmarksSet.add(b.benchmark_name));
      cumulativeBenchmarks.forEach(b => benchmarksSet.add(b.benchmark_name));
    });

    // Second pass: collect data for the selected benchmark type
    sorted.forEach((analysis, roundIndex) => {
      // Use the selected benchmark type (round_benchmarks or cumulative_benchmarks)
      const benchmarkKey = benchmarkTypeToUse === 'round' ? 'round_benchmarks' : 'cumulative_benchmarks';
      const benchmarks = analysis.data?.[benchmarkKey] || [];

      benchmarks.forEach(benchmark => {
        const benchmarkName = benchmark.benchmark_name;

        if (!dataByBenchmark[benchmarkName]) {
          dataByBenchmark[benchmarkName] = {};
        }

        // Process each player's score
        Object.entries(benchmark.scores || {}).forEach(([playerName, scoreData]) => {
          // Get the full model name using the helper function
          const modelName = getModelName(playerName);
          modelsSet.add(modelName);

          if (!dataByBenchmark[benchmarkName][modelName]) {
            dataByBenchmark[benchmarkName][modelName] = [];
          }

          const score = typeof scoreData === 'object' ? scoreData.score : scoreData;
          const explanation = typeof scoreData === 'object' ? scoreData.explanation : null;

          // Only add if we don't already have a score for this round (deduplicate)
          // This prevents duplicates when multiple player name variations map to the same model
          const existingScore = dataByBenchmark[benchmarkName][modelName].find(
            s => s.round === roundIndex + 1
          );

          if (!existingScore) {
            dataByBenchmark[benchmarkName][modelName].push({
              round: roundIndex + 1,
              score: parseFloat(score) || 0,
              explanation: explanation || null
            });
          } else {
            // If we already have a score for this round, take the average
            // (in case the same model appears multiple times with different scores)
            const currentAvg = existingScore.score;
            const newScore = parseFloat(score) || 0;
            existingScore.score = (currentAvg + newScore) / 2;
            // Keep the first explanation if we have one
            if (!existingScore.explanation && explanation) {
              existingScore.explanation = explanation;
            }
          }
        });
      });
    });

    const modelsArray = Array.from(modelsSet);
    console.log('[processRounds] Models in order:', modelsArray);

    return {
      benchmarks: Array.from(benchmarksSet),
      models: modelsArray,
      dataByBenchmark,
      totalRounds: sorted.length
    };
  };


  // Helper to make a color transparent
  const makeTransparent = (color, opacity = 0.15) => {
    // Convert hex to rgba
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    // If already rgba, replace the alpha value
    if (color.startsWith('rgba')) {
      return color.replace(/[\d.]+\)$/g, `${opacity})`);
    }
    return color;
  };

  // Highlight a specific player's line in an SVG chart
  const highlightPlayerInChart = (benchmarkName, playerModelName) => {
    const chartElement = chartRefs.current[benchmarkName];
    if (!chartElement) return null;

    // Find all path elements (lines) in the SVG
    const svgElement = chartElement.querySelector('svg');
    if (!svgElement) return null;

    const paths = svgElement.querySelectorAll('path[stroke]');
    const circles = svgElement.querySelectorAll('circle');
    const originalStyles = [];

    // Get the color assigned to this model
    const highlightedColor = modelColorMap[playerModelName];

    // DEBUG LOGGING
    console.log('[highlightPlayerInChart] === DEBUG ===');
    console.log('[highlightPlayerInChart] benchmarkName:', benchmarkName);
    console.log('[highlightPlayerInChart] playerModelName:', playerModelName);
    console.log('[highlightPlayerInChart] highlightedColor from modelColorMap:', highlightedColor);
    console.log('[highlightPlayerInChart] modelColorMap:', modelColorMap);
    console.log('[highlightPlayerInChart] Number of SVG paths:', paths.length);
    console.log('[highlightPlayerInChart] Number of SVG circles:', circles.length);
    console.log('[highlightPlayerInChart] Path colors:', Array.from(paths).map(p => p.getAttribute('stroke')));

    if (!highlightedColor) {
      console.error('[highlightPlayerInChart] Could not find color for model:', playerModelName);
      return null;
    }

    // Store original styles and highlight the matching path
    paths.forEach((path, index) => {
      const pathColor = path.getAttribute('stroke');

      // Store original style
      originalStyles.push({
        element: path,
        stroke: pathColor,
        strokeWidth: path.getAttribute('stroke-width')
      });

      if (pathColor === highlightedColor) {
        // Highlight this player's line (matched by color)
        console.log('[highlightPlayerInChart] HIGHLIGHTING path at index', index, 'with color', highlightedColor);
        path.setAttribute('stroke-width', '5');
      } else {
        // Make other lines transparent
        path.setAttribute('stroke', makeTransparent(pathColor, 0.15));
        path.setAttribute('stroke-width', '2');
      }
    });

    // Handle circles (data points) - make non-highlighted ones transparent and move highlighted ones on top
    const circlesToMoveOnTop = [];
    circles.forEach((circle) => {
      const circleFill = circle.getAttribute('fill');

      // Store original style
      originalStyles.push({
        element: circle,
        fill: circleFill,
        opacity: circle.getAttribute('opacity') || '1'
      });

      if (circleFill === highlightedColor) {
        // This circle belongs to the highlighted player - keep it opaque and mark for z-index boost
        circlesToMoveOnTop.push(circle);
      } else {
        // Make other circles more transparent
        circle.setAttribute('opacity', '0.15');
      }
    });

    // Move highlighted player's circles to the end of the SVG (renders on top)
    circlesToMoveOnTop.forEach(circle => {
      const parent = circle.parentNode;
      parent.removeChild(circle);
      parent.appendChild(circle);
    });

    console.log('[highlightPlayerInChart] Moved', circlesToMoveOnTop.length, 'circles to top');

    return originalStyles;
  };

  // Restore chart to original state
  const restoreChart = (originalStyles) => {
    if (!originalStyles) return;

    originalStyles.forEach(({ element, stroke, strokeWidth, fill, opacity }) => {
      // Restore path attributes (lines)
      if (stroke !== undefined) {
        element.setAttribute('stroke', stroke);
      }
      if (strokeWidth !== undefined) {
        element.setAttribute('stroke-width', strokeWidth);
      }
      // Restore circle attributes (data points)
      if (fill !== undefined) {
        element.setAttribute('fill', fill);
      }
      if (opacity !== undefined) {
        element.setAttribute('opacity', opacity);
      }
    });
  };

  // Capture chart as image and upload to backend
  const captureAndUploadChart = async (benchmarkName, playerModelName = null) => {
    const chartElement = chartRefs.current[benchmarkName];
    if (!chartElement) {
      console.error('[BenchmarkCharts] Chart element not found:', benchmarkName);
      return null;
    }

    let originalStyles = null;

    try {
      // Highlight player if specified
      if (playerModelName) {
        originalStyles = highlightPlayerInChart(benchmarkName, playerModelName);
        // Wait a bit for DOM to update
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Capture the chart at its natural size with high quality
      const canvas = await html2canvas(chartElement, {
        backgroundColor: colors.bgMedium,
        scale: 2,
        logging: false
      });

      // Scale canvas to 1280x720
      const scaledCanvas = document.createElement('canvas');
      scaledCanvas.width = 1280;
      scaledCanvas.height = 720;
      const ctx = scaledCanvas.getContext('2d');
      ctx.fillStyle = colors.bgMedium;
      ctx.fillRect(0, 0, 1280, 720);
      ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, 1280, 720);

      // Restore chart immediately after capture
      if (originalStyles) {
        restoreChart(originalStyles);
      }

      // Convert scaled canvas to base64 data URL
      const imageData = scaledCanvas.toDataURL('image/png');

      // Upload to backend
      const response = await fetch(`${apiBaseUrl}/api/uploadImage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageData: imageData,
          filename: `chart-${benchmarkName}-${Date.now()}.png`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to upload chart image');
      }

      const result = await response.json();
      console.log('[BenchmarkCharts] Chart uploaded:', result);

      return result; // Returns { filename, fullPath, url }
    } catch (error) {
      console.error('[BenchmarkCharts] Error capturing/uploading chart:', error);
      if (originalStyles) {
        restoreChart(originalStyles);
      }
      return null;
    }
  };

  // Main handler for "Add to Video Stitcher" button
  const handleAddToVideoStitcher = async () => {
    if (!selectedRoundId) {
      alert('Please select a discussion round first');
      return;
    }

    // Save current benchmark type (declare outside try block for catch block access)
    const originalBenchmarkType = benchmarkType;

    try {
      // Switch to 'round' for video stitcher
      if (benchmarkType !== 'round') {
        console.log('[BenchmarkCharts] Temporarily switching to round benchmarks for video stitcher');
        setBenchmarkType('round');
        // Wait for React to reprocess and re-render
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      console.log('[BenchmarkCharts] Fetching analysis for round:', selectedRoundId);

      // Fetch the round analysis
      const response = await fetch(
        `${apiBaseUrl}/api/roundAnalysis?roundId=${selectedRoundId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch round analysis');
      }

      const result = await response.json();
      console.log('[BenchmarkCharts] Full result:', result);
      console.log('[BenchmarkCharts] Result keys:', Object.keys(result));

      const analysis = result.analysis;

      // Get round number from local discussionRounds list
      const selectedRound = discussionRounds.find(r => r.id === selectedRoundId);
      const roundNumber = selectedRound ? selectedRound.number : null;

      console.log('[BenchmarkCharts] Round analysis:', analysis);
      console.log('[BenchmarkCharts] Round number from local list:', roundNumber);

      // Get interesting findings (sorted by rank)
      const findings = (analysis.interesting_findings || [])
        .sort((a, b) => a.rank - b.rank)
        .slice(0, 3); // Top 3 findings

      if (findings.length === 0) {
        alert('No interesting findings for this round');
        return;
      }

      console.log('[BenchmarkCharts] Processing', findings.length, 'findings');

      // Save current benchmark selection to restore later
      const originalBenchmark = selectedBenchmark;

      // Note: maxRoundFilter is already set by the useEffect based on selectedRoundId
      console.log('[BenchmarkCharts] Current maxRoundFilter:', maxRoundFilter, 'roundNumber:', roundNumber);

      // Process each finding
      const chartImages = [];
      for (const finding of findings) {
        const benchmarkName = finding.benchmark_name;

        // DEBUG: Log finding details
        console.log('[BenchmarkCharts] === FINDING DEBUG ===');
        console.log('[BenchmarkCharts] finding:', finding);
        console.log('[BenchmarkCharts] finding.player_name:', finding.player_name);
        console.log('[BenchmarkCharts] finding.finding_type:', finding.finding_type);

        const playerModelName = finding.finding_type === 'player_specific' && finding.player_name
          ? getModelName(finding.player_name)
          : null;

        console.log('[BenchmarkCharts] After getModelName:', playerModelName);
        console.log('[BenchmarkCharts] playerToModelMap:', playerToModelMap);
        console.log('[BenchmarkCharts] Capturing chart:', benchmarkName, 'for player:', playerModelName);

        // Switch to this benchmark so the chart is rendered
        setSelectedBenchmark(benchmarkName);

        // Wait for React to render the chart
        await new Promise(resolve => setTimeout(resolve, 300));

        // Capture and upload the chart
        const uploadResult = await captureAndUploadChart(benchmarkName, playerModelName);

        if (uploadResult) {
          chartImages.push({
            filename: uploadResult.filename,
            fullPath: uploadResult.fullPath,
            url: uploadResult.url,
            voiceoverText: finding.explanation
          });
        }
      }

      // Restore original benchmark selection
      setSelectedBenchmark(originalBenchmark);

      // Restore original benchmark type
      if (originalBenchmarkType !== benchmarkType) {
        console.log('[BenchmarkCharts] Restoring original benchmark type:', originalBenchmarkType);
        setBenchmarkType(originalBenchmarkType);
      }

      console.log('[BenchmarkCharts] Dispatching charts to video stitcher:', chartImages);

      // Dispatch custom event to VideoList
      window.dispatchEvent(new CustomEvent('addChartsToVideoStitcher', {
        detail: { charts: chartImages }
      }));

      //alert(`Added ${chartImages.length} chart${chartImages.length !== 1 ? 's' : ''} to video stitcher!`);
    } catch (error) {
      console.error('[BenchmarkCharts] Error adding charts to video stitcher:', error);

      // Restore original benchmark type on error
      if (typeof originalBenchmarkType !== 'undefined' && originalBenchmarkType !== benchmarkType) {
        console.log('[BenchmarkCharts] Error occurred, restoring original benchmark type:', originalBenchmarkType);
        setBenchmarkType(originalBenchmarkType);
      }

      alert('Failed to add charts to video stitcher: ' + error.message);
    }
  };

  const handleChartDragStart = (e, benchmarkName) => {
    const chartElement = chartRefs.current[benchmarkName];
    if (!chartElement) return;

    // Store the element reference (NOT the screenshot) - we'll capture it on drop
    window.draggedChartElement = chartElement;
    window.draggedChartName = benchmarkName;

    // Set drag data to indicate this is a chart
    e.dataTransfer.setData('chart-drag', 'true');
    e.dataTransfer.effectAllowed = 'copy';

    console.log('[BenchmarkCharts] Chart drag started:', benchmarkName);
  };

  // Create mapping from model name to nickname
  const modelToNicknameMap = {};
  users.forEach(user => {
    if (user.name && user.model) {
      // Extract nickname from name (e.g., "Kimi (Jinx)" -> "Kimi")
      const baseName = user.name.split('(')[0].trim();
      modelToNicknameMap[user.model] = baseName;
    }
  });

  // Helper function to download table HTML as file
  const downloadTableHTML = (benchmarkName, data) => {
    const maxRound = (maxRoundFilter !== null && maxRoundFilter !== undefined)
      ? maxRoundFilter
      : benchmarkData.totalRounds;

    // Get all rounds present in the data (up to maxRound)
    const roundsSet = new Set();
    Object.values(data).forEach(points => {
      points.forEach(point => {
        if (point.round <= maxRound) {
          roundsSet.add(point.round);
        }
      });
    });
    const rounds = Array.from(roundsSet).sort((a, b) => a - b);

    // Sort models by most recent score (descending)
    const sortedModels = Object.entries(data)
      .map(([modelName, points]) => {
        const lastScore = points.length > 0 ? points[points.length - 1].score : 0;
        return { modelName, lastScore };
      })
      .sort((a, b) => b.lastScore - a.lastScore)
      .map(item => item.modelName);

    // Generate complete HTML document with inline styles
    let html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${benchmarkName} - Benchmark Data</title>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; padding: 20px; background: #f9fafb;">
  <h1 style="color: #1f2937; margin-bottom: 20px;">${benchmarkName}</h1>
  <table style="border-collapse: collapse; width: 100%; background: white; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);">
    <thead>
      <tr>
        <th style="padding: 12px; text-align: left; background: #f3f4f6; border: 1px solid #e5e7eb; font-weight: 600; color: #374151;">Model</th>`;

    rounds.forEach(round => {
      html += `
        <th style="padding: 12px; text-align: center; background: #f3f4f6; border: 1px solid #e5e7eb; font-weight: 600; color: #374151;">R${round}</th>`;
    });

    html += `
      </tr>
    </thead>
    <tbody>`;

    sortedModels.forEach(modelName => {
      const points = data[modelName];
      const nickname = modelToNicknameMap[modelName] || modelName.split('/')[1] || modelName;

      html += `
      <tr>
        <td style="padding: 12px; border: 1px solid #e5e7eb;">
          <div style="font-weight: 600; color: #1f2937;">${nickname}</div>
          <div style="font-size: 0.85em; color: #6b7280;">${modelName}</div>
        </td>`;

      rounds.forEach(round => {
        const point = points.find(p => p.round === round);
        const score = point ? point.score.toFixed(1) : '—';
        html += `
        <td style="padding: 12px; border: 1px solid #e5e7eb; text-align: center; font-weight: 500;">${score}</td>`;
      });

      html += `
      </tr>`;
    });

    html += `
    </tbody>
  </table>
</body>
</html>`;

    // Create blob and download
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Create filename from benchmark name (replace spaces with hyphens, remove special chars)
    const filename = benchmarkName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-') + '.html';
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Helper function to render data table
  const renderDataTable = (benchmarkName, data) => {
    // Use explicit null/undefined check to allow 0 as a valid filter value
    const maxRound = (maxRoundFilter !== null && maxRoundFilter !== undefined)
      ? maxRoundFilter
      : benchmarkData.totalRounds;

    // Get all rounds present in the data (up to maxRound)
    const roundsSet = new Set();
    Object.values(data).forEach(points => {
      points.forEach(point => {
        if (point.round <= maxRound) {
          roundsSet.add(point.round);
        }
      });
    });
    const rounds = Array.from(roundsSet).sort((a, b) => a - b);

    // Sort models by most recent score (descending)
    const sortedModels = Object.entries(data)
      .map(([modelName, points]) => {
        const lastScore = points.length > 0 ? points[points.length - 1].score : 0;
        return { modelName, lastScore };
      })
      .sort((a, b) => b.lastScore - a.lastScore)
      .map(item => item.modelName);

    return (
      <div style={{
        marginTop: '20px',
        background: colors.bgLight,
        padding: '20px',
        borderRadius: '8px',
        border: `1px solid ${colors.purple}40`,
        overflowX: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <div style={{
            fontSize: '1em',
            fontWeight: 'bold',
            color: colors.lightPurple
          }}>
            📊 {benchmarkName} - Data Table
          </div>
          <button
            onClick={() => downloadTableHTML(benchmarkName, data)}
            style={{
              padding: '6px 12px',
              background: `linear-gradient(135deg, ${colors.purple} 0%, ${colors.pink} 100%)`,
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85em',
              fontWeight: 'bold',
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(139, 92, 246, 0.3)';
            }}
          >
            💾 Download HTML
          </button>
        </div>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.85em'
        }}>
          <thead>
            <tr>
              <th style={{
                padding: '10px',
                borderBottom: `2px solid ${colors.purple}`,
                textAlign: 'left',
                color: colors.lightPurple,
                fontWeight: 'bold',
                position: 'sticky',
                left: 0,
                background: colors.bgLight,
                zIndex: 1
              }}>
                Model
              </th>
              {rounds.map(round => (
                <th key={round} style={{
                  padding: '10px',
                  borderBottom: `2px solid ${colors.purple}`,
                  textAlign: 'center',
                  color: colors.lightPurple,
                  fontWeight: 'bold',
                  minWidth: '60px'
                }}>
                  R{round}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedModels.map((modelName, modelIndex) => {
              const points = data[modelName];
              const color = modelColorMap[modelName] || getModelColor(modelIndex);

              return (
                <tr key={modelName} style={{
                  borderBottom: `1px solid ${colors.purple}20`
                }}>
                  <td style={{
                    padding: '10px',
                    fontWeight: '600',
                    color: '#f3f4f6',
                    position: 'sticky',
                    left: 0,
                    background: colors.bgLight,
                    zIndex: 1,
                    borderRight: `1px solid ${colors.purple}20`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: color,
                        flexShrink: 0
                      }} />
                      <div>
                        <div style={{ fontSize: '0.9em' }}>
                          {modelToNicknameMap[modelName] || modelName.split('/')[1] || modelName}
                        </div>
                        <div style={{ fontSize: '0.75em', color: '#6b7280' }}>
                          {modelName}
                        </div>
                      </div>
                    </div>
                  </td>
                  {rounds.map(round => {
                    const point = points.find(p => p.round === round);
                    const score = point ? point.score : null;

                    return (
                      <td key={round} style={{
                        padding: '10px',
                        textAlign: 'center',
                        color: score !== null ? '#f3f4f6' : '#6b7280',
                        fontWeight: score !== null ? '600' : '400',
                        background: score !== null ? `${color}15` : 'transparent'
                      }}>
                        {score !== null ? score.toFixed(1) : '—'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderLineChart = (benchmarkName, data) => {
    // SVG dimensions - slightly thinner chart for better proportions
    const width = 720;
    const height = 330;
    const padding = { top: 20, right: 280, bottom: 40, left: 60 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Find max values for scaling
    // Use explicit null/undefined check to allow 0 as a valid filter value
    const maxRound = (maxRoundFilter !== null && maxRoundFilter !== undefined)
      ? maxRoundFilter
      : benchmarkData.totalRounds;

    console.log('[renderLineChart]', benchmarkName, '- maxRoundFilter:', maxRoundFilter, 'maxRound:', maxRound, 'totalRounds:', benchmarkData.totalRounds);

    // Filter data based on maxRoundFilter
    const filteredData = {};
    Object.entries(data).forEach(([modelName, points]) => {
      const originalLength = points.length;
      filteredData[modelName] = points.filter(p => p.round <= maxRound);
      console.log('[renderLineChart]', benchmarkName, modelName, '- filtered from', originalLength, 'to', filteredData[modelName].length, 'points');
    });

    // Find the minimum round number in the filtered data
    let minRound = Infinity;
    Object.values(filteredData).forEach(points => {
      points.forEach(point => {
        minRound = Math.min(minRound, point.round);
      });
    });
    // Default to 1 if no data
    if (!isFinite(minRound)) minRound = 1;

    // Calculate dynamic Y-axis range based on actual scores (filtered)
    let minScore = 10;
    let maxScore = 0;
    Object.values(filteredData).forEach(points => {
      points.forEach(point => {
        minScore = Math.min(minScore, point.score);
        maxScore = Math.max(maxScore, point.score);
      });
    });

    // Add some padding to the range (10% on each side)
    const scoreRange = maxScore - minScore;
    const padding_percent = 0.1;
    minScore = Math.max(0, minScore - scoreRange * padding_percent);
    maxScore = Math.min(10, maxScore + scoreRange * padding_percent);

    // Ensure we have at least a small range to display
    if (maxScore - minScore < 0.5) {
      minScore = Math.max(0, minScore - 0.25);
      maxScore = Math.min(10, maxScore + 0.25);
    }

    // Scale functions - map rounds so minRound is at left edge, maxRound is at right edge
    const roundRange = maxRound - minRound;
    const scaleX = (round) => {
      if (roundRange === 0) return padding.left + chartWidth / 2; // Center if only one round
      return ((round - minRound) / roundRange) * chartWidth + padding.left;
    };
    const scaleY = (score) => height - padding.bottom - ((score - minScore) / (maxScore - minScore)) * chartHeight;

    // Generate grid lines based on dynamic range
    const gridLines = [];
    const numGridLines = 5;
    const scoreStep = (maxScore - minScore) / numGridLines;

    for (let i = 0; i <= numGridLines; i++) {
      const score = minScore + (scoreStep * i);
      const y = scaleY(score);
      gridLines.push(
        <g key={`grid-${i}`}>
          <line
            x1={padding.left}
            y1={y}
            x2={width - padding.right}
            y2={y}
            stroke="rgba(139, 92, 246, 0.15)"
            strokeWidth="1"
          />
          <text
            x={padding.left - 10}
            y={y + 4}
            fill="#9ca3af"
            fontSize="11"
            textAnchor="end"
          >
            {score.toFixed(1)}
          </text>
        </g>
      );
    }

    // Generate x-axis labels (from minRound to maxRound)
    const xAxisLabels = [];
    for (let i = minRound; i <= maxRound; i++) {
      xAxisLabels.push(
        <text
          key={`x-label-${i}`}
          x={scaleX(i)}
          y={height - padding.bottom + 20}
          fill="#9ca3af"
          fontSize="11"
          textAnchor="middle"
        >
          {i}
        </text>
      );
    }

    return (
      <div>
        {/* Draggable chart container - ONLY includes title and SVG */}
        <div
          ref={(el) => { chartRefs.current[benchmarkName] = el; }}
          draggable="true"
          onDragStart={(e) => handleChartDragStart(e, benchmarkName)}
          style={{
            background: colors.bgLight,
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '12px',
            border: `2px solid transparent`,
            backgroundImage: `
              linear-gradient(135deg, ${colors.bgLight} 0%, ${colors.bgMedium} 100%),
              linear-gradient(135deg, ${colors.purple} 0%, ${colors.pink} 100%)
            `,
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            boxShadow: `0 8px 24px rgba(139, 92, 246, 0.2)`,
            cursor: 'grab',
            // Fixed dimensions with good proportions for chart + legend
            width: '760px',
            height: '380px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column'
          }}
          onMouseDown={(e) => { e.currentTarget.style.cursor = 'grabbing'; }}
          onMouseUp={(e) => { e.currentTarget.style.cursor = 'grab'; }}
        >
          <div style={{
            fontWeight: 'bold',
            fontSize: '1.15em',
            marginBottom: '16px',
            /*background: `linear-gradient(135deg, ${colors.lightPurple} 0%, ${colors.lightPink} 100%)`,*/
            /*WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',*/
            filter: 'drop-shadow(0 0 8px rgba(139, 92, 246, 0.3))',
            color: colors.lightPurple
          }}>
            {benchmarkName}
          </div>

        {/* SVG container that fills remaining flex space */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
          <svg width={width} height={height} style={{ overflow: 'visible' }}>
          {/* Grid lines */}
          {gridLines}

          {/* X-axis */}
          <line
            x1={padding.left}
            y1={height - padding.bottom}
            x2={width - padding.right}
            y2={height - padding.bottom}
            stroke="rgba(139, 92, 246, 0.3)"
            strokeWidth="2"
          />
          <text
            x={padding.left + chartWidth / 2}
            y={height - 5}
            fill={colors.lightPurple}
            fontSize="12"
            textAnchor="middle"
            fontWeight="bold"
          >
            Round
          </text>

          {/* Y-axis */}
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={height - padding.bottom}
            stroke="rgba(139, 92, 246, 0.3)"
            strokeWidth="2"
          />
          <text
            x={15}
            y={padding.top + chartHeight / 2}
            fill={colors.lightPurple}
            fontSize="12"
            textAnchor="middle"
            fontWeight="bold"
            transform={`rotate(-90, 15, ${padding.top + chartHeight / 2})`}
          >
            Score ({minScore.toFixed(1)}-{maxScore.toFixed(1)})
          </text>

          {/* X-axis labels */}
          {xAxisLabels}

          {/* Lines for each model */}
          {Object.entries(filteredData)
            .sort((a, b) => {
              // Sort by most recent score (descending)
              const [, pointsA] = a;
              const [, pointsB] = b;

              // Get the most recent score for each model
              const lastScoreA = pointsA.length > 0 ? pointsA[pointsA.length - 1].score : 0;
              const lastScoreB = pointsB.length > 0 ? pointsB[pointsB.length - 1].score : 0;

              // Sort descending (highest score first)
              return lastScoreB - lastScoreA;
            })
            .map(([modelName, points], modelIndex) => {
            const color = modelColorMap[modelName] || getModelColor(modelIndex);

            // Generate path
            const pathData = points.map((point, i) => {
              const x = scaleX(point.round);
              const y = scaleY(point.score);
              return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
            }).join(' ');

            // Determine if this model should be faded
            const isFocused = !focusedModel || focusedModel === modelName;
            const opacity = isFocused ? 1 : 0.15;

            return (
              <g key={modelName} style={{ opacity }}>
                {/* Line */}
                <path
                  d={pathData}
                  fill="none"
                  stroke={color}
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  style={{
                    filter: `drop-shadow(0 0 4px ${color}80)`
                  }}
                />

                {/* Points */}
                {points.map((point, i) => (
                  <circle
                    key={i}
                    cx={scaleX(point.round)}
                    cy={scaleY(point.score)}
                    r="4"
                    fill={color}
                    stroke="rgba(10, 10, 20, 0.9)"
                    strokeWidth="2"
                    style={{
                      filter: `drop-shadow(0 0 6px ${color})`,
                      cursor: point.explanation ? 'pointer' : 'default'
                    }}
                    onClick={() => {
                      if (point.explanation) {
                        // Toggle: if clicking the same point, deselect it and clear filter
                        if (selectedPoint &&
                            selectedPoint.benchmarkName === benchmarkName &&
                            selectedPoint.modelName === modelName &&
                            selectedPoint.round === point.round) {
                          setSelectedPoint(null);
                          setMaxRoundFilter(null);
                        } else {
                          setSelectedPoint({
                            benchmarkName,
                            modelName,
                            round: point.round,
                            score: point.score,
                            explanation: point.explanation
                          });
                          // Filter to show only rounds up to this point
                          setMaxRoundFilter(point.round);
                        }
                      }
                    }}
                  />
                ))}

                {/* Legend - Score on left, names stacked on right */}
                <g
                  transform={`translate(${width - padding.right + 50}, ${padding.top + modelIndex * 36 + -20})`}
                  onClick={() => {
                    // Toggle focus: if clicking the same model, unfocus; otherwise focus the new model
                    setFocusedModel(focusedModel === modelName ? null : modelName);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <line
                    x1="0"
                    y1="10"
                    x2="20"
                    y2="10"
                    stroke={color}
                    strokeWidth="2.5"
                  />
                  <circle
                    cx="10"
                    cy="10"
                    r="3"
                    fill={color}
                    stroke="rgba(10, 10, 20, 0.9)"
                    strokeWidth="1"
                  />
                  {/* Score (large, on left) */}
                  <text
                    x="25"
                    y="14"
                    fontSize="18"
                    fill="#9ca3af"
                    fontWeight="700"
                  >
                    {points.length > 0 ? points[points.length - 1].score.toFixed(1) : '—'}
                  </text>
                  {/* Nickname (larger, whiter, top right) */}
                  <text
                    x="65"
                    y="7"
                    fontSize="14"
                    fill="#f3f4f6"
                    fontWeight="700"
                  >
                    {modelToNicknameMap[modelName] || modelName.split('/')[1] || modelName}
                  </text>
                  {/* Full model name (smaller, bottom right) */}
                  <text
                    x="65"
                    y="20"
                    fontSize="9"
                    fill="#6b7280"
                    fontWeight="500"
                  >
                    {modelName}
                  </text>
                </g>
              </g>
            );
          })}
        </svg>
        </div>

        {/* Compact explanation - INSIDE draggable area, single line, no title */}
        {selectedPoint && selectedPoint.benchmarkName === benchmarkName && (
          <div style={{
            padding: '6px 12px',
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '6px',
            fontSize: '11px',
            color: '#d1d5db',
            marginTop: '8px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            <span style={{ color: colors.lightPurple, fontWeight: '600' }}>
              {selectedPoint.modelName}
            </span>
            {' • '}
            <span style={{ color: '#9ca3af' }}>
              R{selectedPoint.round} ({selectedPoint.score.toFixed(1)})
            </span>
            {' • '}
            {selectedPoint.explanation}
          </div>
        )}
        </div>

        {/* Filter indicator button - OUTSIDE draggable area, below chart */}
        {maxRoundFilter && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '12px',
            padding: '8px 12px',
            background: 'rgba(139, 92, 246, 0.15)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '6px',
            fontSize: '13px',
            width: '760px'
          }}>
            <span style={{ color: colors.lightPurple, fontWeight: '500' }}>
              📊 Showing rounds 1-{maxRoundFilter} of {benchmarkData.totalRounds}
            </span>
            <button
              onClick={() => {
                setMaxRoundFilter(null);
                setSelectedPoint(null);
              }}
              style={{
                padding: '4px 10px',
                background: 'rgba(236, 72, 153, 0.2)',
                border: '1px solid rgba(236, 72, 153, 0.4)',
                borderRadius: '4px',
                color: colors.lightPink,
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(236, 72, 153, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(236, 72, 153, 0.2)';
              }}
            >
              Show All Rounds
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        background: colors.bgLight,
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px',
        textAlign: 'center',
        color: colors.lightPurple
      }}>
        Loading benchmark history...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: colors.bgLight,
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px',
        textAlign: 'center',
        color: '#ef4444'
      }}>
        Error loading benchmarks: {error}
      </div>
    );
  }

  if (!benchmarkData || benchmarkData.benchmarks.length === 0) {
    return null; // No benchmarks to display
  }

  return (
    <div style={{
      background: `linear-gradient(135deg, ${colors.bgDark} 0%, ${colors.bgMedium} 100%)`,
      border: `2px solid transparent`,
      backgroundImage: `
        linear-gradient(135deg, ${colors.bgDark} 0%, ${colors.bgMedium} 100%),
        linear-gradient(135deg, ${colors.purple} 0%, ${colors.pink} 100%)
      `,
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      boxShadow: '0 8px 24px rgba(139, 92, 246, 0.3)'
    }}>
      {/* Video Stitcher Controls - Always Visible at Top */}
      {discussionRounds.length > 0 && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '2px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <span style={{
            fontSize: '0.9em',
            color: '#10b981',
            fontWeight: '700'
          }}>
            🎬 Video Stitcher:
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontSize: '0.85em',
              color: '#e5e7eb',
              fontWeight: '600'
            }}>
              Round:
            </span>
            <select
              value={selectedRoundId || ''}
              onChange={(e) => {
                const newId = e.target.value;
                console.log('[BenchmarkCharts] Round selected:', newId);
                setSelectedRoundId(newId);
              }}
              style={{
                background: 'rgba(139, 92, 246, 0.2)',
                color: '#ffffff',
                border: '2px solid rgba(139, 92, 246, 0.3)',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85em',
                fontWeight: '600',
                minWidth: '150px'
              }}
            >
              <option value="">Select Round</option>
              {discussionRounds.map((round, index) => (
                <option key={round.id} value={round.id}>
                  Round {index + 1}
                </option>
              ))}
            </select>
          </div>

          {selectedRoundId && (
            <button
              onClick={() => {
                console.log('[BenchmarkCharts] Button clicked! selectedRoundId:', selectedRoundId);
                handleAddToVideoStitcher();
              }}
              style={{
                background: `linear-gradient(135deg, #10b981 0%, #059669 100%)`,
                color: '#ffffff',
                border: '2px solid rgba(16, 185, 129, 0.5)',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9em',
                fontWeight: '700',
                transition: 'all 0.2s',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
              }}
            >
              Add Top 3 Charts
            </button>
          )}

          <div style={{
            fontSize: '0.75em',
            color: '#9ca3af',
            marginLeft: 'auto'
          }}>
            Automatically adds top 3 interesting findings with voiceover
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div style={{
            background: `linear-gradient(135deg, ${colors.darkPurple} 0%, #6b21a8 100%)`,
            color: '#ffffff',
            padding: '10px 20px',
            borderRadius: '8px',
            fontSize: '0.8em',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
            border: '1px solid rgba(139, 92, 246, 0.3)'
          }}>
            📈 Charts
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {/* Benchmark selector dropdown */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                fontSize: '0.9em',
                color: colors.lightPurple,
                fontWeight: '600'
              }}>
                Benchmark:
              </span>
              <select
                value={selectedBenchmark || ''}
                onChange={(e) => setSelectedBenchmark(e.target.value)}
                style={{
                  background: 'rgba(139, 92, 246, 0.2)',
                  color: '#ffffff',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85em',
                  fontWeight: '600',
                  minWidth: '200px'
                }}
              >
                {benchmarkData.benchmarks.map((benchmark) => (
                  <option key={benchmark} value={benchmark}>
                    {benchmark}
                  </option>
                ))}
              </select>
            </div>

            {/* Benchmark type selector (round vs cumulative) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                fontSize: '0.9em',
                color: colors.lightPurple,
                fontWeight: '600'
              }}>
                Data Type:
              </span>
              <select
                value={benchmarkType}
                onChange={(e) => setBenchmarkType(e.target.value)}
                style={{
                  background: 'rgba(139, 92, 246, 0.2)',
                  color: '#ffffff',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85em',
                  fontWeight: '600',
                  minWidth: '150px'
                }}
              >
                <option value="round">Round Performance</option>
                <option value="cumulative">Cumulative Game</option>
              </select>
            </div>

            <div style={{
              fontSize: '0.85em',
              color: '#9ca3af',
              marginLeft: 'auto'
            }}>
              {benchmarkData.totalRounds} round{benchmarkData.totalRounds !== 1 ? 's' : ''} analyzed
            </div>
          </div>
        </div>

      </div>

      {/* Render chart for selected benchmark */}
      {selectedBenchmark && benchmarkData.dataByBenchmark[selectedBenchmark] && (
        <div>
          {renderLineChart(selectedBenchmark, benchmarkData.dataByBenchmark[selectedBenchmark])}
          {renderDataTable(selectedBenchmark, benchmarkData.dataByBenchmark[selectedBenchmark])}
        </div>
      )}
    </div>
  );
};

export default BenchmarkCharts;
